import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Layout, Menu, Button, Avatar, Dropdown, message } from 'antd'
import type { MenuProps } from 'antd'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import {
  LogOut,
  Settings,
  ChevronLeft,
  ChevronRight,
  Menu as MenuIcon,
  X,
  User as UserIcon,
} from 'lucide-react'
import ErrorBoundary from '../components/ErrorBoundary'
import './BasicLayout.css'

const { Header, Sider, Content } = Layout
const MOBILE_BREAKPOINT = 768

export interface BaseRouteItem {
  path: string
  label: string
  icon?: string
  children?: BaseRouteItem[]
}

export interface BasicLayoutProps {
  brandTitle?: string
  brandSubtitle?: string
  brandLetter?: string
  versionText?: string
  greeting?: string
  statusText?: string
  userName?: string
  userRole?: string
  userAvatarText?: string
  routeConfig: any[]
  getMenuItem?: (item: any) => any
  iconMap?: Record<string, React.ReactNode>
  onLogout?: () => Promise<void> | void
  loginPath?: string
  children?: React.ReactNode
}

export const BasicLayout: React.FC<BasicLayoutProps> = ({
  brandTitle = 'CStore',
  brandSubtitle = 'COMMAND',
  brandLetter = 'C',
  versionText = 'v3.0.0 Command OS',
  greeting = '运营指挥大厅',
  statusText = '核心服务正常运行',
  userName = 'Administrator',
  userRole = '平台主控',
  userAvatarText = 'ADM',
  routeConfig = [],
  getMenuItem = (item: any) => ({
    key: item.path,
    label: item.label,
    children: item.children?.map(getMenuItem),
  }),
  iconMap = {},
  onLogout,
  loginPath = '/login',
  children,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < MOBILE_BREAKPOINT : false))
  const [collapsed, setCollapsed] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < MOBILE_BREAKPOINT : false))
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT
      setIsMobile(mobile)
      if (!mobile) setMobileMenuOpen(false)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const menuItems: MenuProps['items'] = useMemo(
    () =>
      routeConfig.map((item: any) => ({
        ...getMenuItem(item),
        icon: item.icon && iconMap[item.icon] ? iconMap[item.icon] : undefined,
      })),
    [routeConfig, getMenuItem, iconMap],
  )

  const userMenuItems: MenuProps['items'] = useMemo(
    () => [
      { key: 'profile', icon: <UserIcon size={16} />, label: '管理员信息' },
      { key: 'settings', icon: <Settings size={16} />, label: '系统设置' },
      { type: 'divider' as const },
      { key: 'logout', icon: <LogOut size={16} />, label: '退出系统', danger: true },
    ],
    [],
  )

  const handleMenuClick: MenuProps['onClick'] = useCallback(
    async ({ key }: { key: string }) => {
      if (key === 'logout') {
        try {
          if (onLogout) {
            await onLogout()
          }
          message.success('已安全退出')
        } catch {
          // ignore
        } finally {
          navigate(loginPath, { replace: true })
        }
        return
      }
      if (key === 'settings' || key === 'profile') {
        message.info('当前已为最高权限管理员')
        return
      }
      navigate(key)
      if (isMobile) {
        setMobileMenuOpen(false)
      }
    },
    [navigate, isMobile, onLogout, loginPath],
  )

  const selectedKeys = useMemo(() => [location.pathname], [location.pathname])

  const defaultOpenKeys = useMemo(() => {
    for (const item of (routeConfig as any[])) {
      if (item.children?.some((c: any) => c.path === location.pathname)) {
        return [item.path]
      }
    }
    return []
  }, [routeConfig, location.pathname])

  return (
    <Layout className="command-layout">
      {isMobile && mobileMenuOpen && (
        <div className="mobile-sider-mask" onClick={() => setMobileMenuOpen(false)} />
      )}

      <Sider
        trigger={null}
        collapsible
        collapsed={isMobile ? false : collapsed}
        width={256}
        collapsedWidth={isMobile ? 0 : 80}
        theme="light"
        className={`command-sider ${isMobile ? 'mobile-sider' : ''} ${mobileMenuOpen ? 'mobile-open' : ''}`}
      >
        <div className="sider-brand">
          <div className="brand-mark">
            <span className="brand-letter">{brandLetter}</span>
            <span className="brand-pulse" />
          </div>
          {!collapsed && (
            <div className="brand-text">
              <span className="brand-title">{brandTitle}</span>
              <span className="brand-subtitle">{brandSubtitle}</span>
            </div>
          )}
        </div>

        {!isMobile && (
          <button
            type="button"
            className="sider-collapse-btn"
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? '展开侧边栏' : '收起侧边栏'}
          >
            {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </button>
        )}

        <Menu
          mode="inline"
          selectedKeys={selectedKeys}
          defaultOpenKeys={collapsed ? [] : defaultOpenKeys}
          items={menuItems}
          onClick={handleMenuClick}
          className="command-menu"
        />

        {!collapsed && (
          <div className="sider-footer">
            <div className="status-line">
              <span className="status-dot active" />
              <span className="status-label">{statusText}</span>
            </div>
            <div className="version-label">{versionText}</div>
          </div>
        )}
      </Sider>

      <Layout className="command-main">
        <Header className="command-header">
          <div className="header-breadcrumb">
            {isMobile && (
              <Button
                type="text"
                icon={mobileMenuOpen ? <X size={20} /> : <MenuIcon size={20} />}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="header-icon-btn mobile-menu-toggle"
              />
            )}
            <span className="command-greeting">{greeting}</span>
            <span className="command-divider" />
            <span className="command-date">
              {new Date().toLocaleDateString('zh-CN', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                weekday: 'long',
              })}
            </span>
          </div>

          <div className="header-actions">
            <Dropdown
              menu={{ items: userMenuItems, onClick: handleMenuClick }}
              placement="bottomRight"
              overlayClassName="command-dropdown"
            >
              <div className="user-profile">
                <Avatar className="user-avatar">{userAvatarText}</Avatar>
                <div className="user-info">
                  <span className="user-name">{userName}</span>
                  <span className="user-role">{userRole}</span>
                </div>
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content className="command-content">
          <div className="content-inner">
            <ErrorBoundary>{children || <Outlet />}</ErrorBoundary>
          </div>
        </Content>
      </Layout>
    </Layout>
  )
}

export default BasicLayout
